import { z } from 'zod';

/**
 * Validated environment configuration.
 *
 * Every `import.meta.env` read in the application happens here and nowhere
 * else (§80). Two reasons:
 *
 *  1. A missing or malformed variable fails loudly at startup with a message
 *     naming the variable, instead of surfacing later as `undefined` inside an
 *     axios base URL.
 *  2. There is exactly one place to look to learn what the app needs to run.
 *
 * SECURITY: anything prefixed `VITE_` is inlined into the client bundle and is
 * readable by anyone with devtools. Never put a secret here — no vendor API
 * keys, no payment credentials, no backend service passwords (§71).
 */
const envSchema = z.object({
  VITE_API_URL: z.url({
    message: 'VITE_API_URL must be a valid URL, e.g. http://localhost:45000/api',
  }),

  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),

  // Vite exposes booleans as strings; coerce explicitly rather than relying on
  // the truthiness of the string "false".
  VITE_ENABLE_MSW: z
    .string()
    .optional()
    .transform((value) => value === 'true'),

  // Minutes without interaction before the user is signed out. Unset = 15.
  VITE_IDLE_TIMEOUT_MINUTES: z
    .string()
    .optional()
    .transform((value) => (value ? Number(value) : 15))
    .pipe(
      z
        .number({ message: 'VITE_IDLE_TIMEOUT_MINUTES must be a number of minutes, e.g. 15' })
        .int('VITE_IDLE_TIMEOUT_MINUTES must be a whole number of minutes')
        .positive('VITE_IDLE_TIMEOUT_MINUTES must be greater than 0'),
    ),
});

function parseEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');

    throw new Error(
      `Invalid environment configuration:\n${issues}\n\n` +
        'Copy .env.example to .env.development and fill in the missing values.',
    );
  }

  return result.data;
}

const parsed = parseEnv();

export const env = {
  apiUrl: parsed.VITE_API_URL,
  appEnv: parsed.VITE_APP_ENV,
  enableMsw: parsed.VITE_ENABLE_MSW,
  idleTimeoutMinutes: parsed.VITE_IDLE_TIMEOUT_MINUTES,
  isProduction: parsed.VITE_APP_ENV === 'production',
  isDevelopment: parsed.VITE_APP_ENV === 'development',
} as const;

export type Env = typeof env;
